import React, { useEffect, useMemo, useState } from "react";
import { Clock, Bot, RefreshCw, AlertCircle, Search, X, BarChart3 } from "lucide-react";
import LotteryLogo from "./LotteryLogo";
import { fetchLotteries } from "../api";
import { useSearch } from "../context/SearchContext";

// Brand → colored top bar style (matches loteriasdominicanas.com look)
const BRAND_BAR = {
  nacional:        { bg: "#22a04b", fg: "#ffffff", label: "Nacional" },
  leidsa:          { bg: "#f6c91f", fg: "#1c2c6a", label: "Leidsa" },
  loteka:          { bg: "#00bcd4", fg: "#ffffff", label: "Loteka" },
  "loto-real":     { bg: "#1c2c6a", fg: "#ffffff", label: "Lotería Real" },
  americanas:      { bg: "#3e3f6e", fg: "#ffffff", label: "Americanas" },
  "la-primera":    { bg: "#e94560", fg: "#ffffff", label: "La Primera" },
  "la-suerte":     { bg: "#1d63c1", fg: "#ffffff", label: "La Suerte" },
  lotedom:         { bg: "#0e3477", fg: "#ffffff", label: "LoteDom" },
  anguila:         { bg: "#f08124", fg: "#ffffff", label: "Anguila" },
  "king-lottery":  { bg: "#f08124", fg: "#ffffff", label: "King Lottery" },
};

function brandFor(lotteryId, fallbackName, colors) {
  if (BRAND_BAR[lotteryId]) return BRAND_BAR[lotteryId];
  return { bg: (colors && colors[0]) || "#0e4a7a", fg: "#ffffff", label: fallbackName || "" };
}

function dayMonthFromDate(isoDate) {
  // Accept "YYYY-MM-DD" or ISO datetime; return "DD-MM" in DR local time.
  if (!isoDate) return "";
  // If we got an ISO datetime, parse it and shift to DR (UTC-4) before extracting day/month
  if (/T\d/.test(String(isoDate))) {
    try {
      const d = new Date(isoDate);
      const dr = new Date(d.getTime() - 4 * 60 * 60 * 1000);
      const dd = String(dr.getUTCDate()).padStart(2, "0");
      const mm = String(dr.getUTCMonth() + 1).padStart(2, "0");
      return `${dd}-${mm}`;
    } catch { return ""; }
  }
  const m = String(isoDate).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return "";
  return `${m[3]}-${m[2]}`;
}

// A draw is "today's result" when the lottery's scheduled time has already
// passed in Dominican Republic local time (GMT-4). Otherwise the numbers
// shown are still from the previous day.
function isTodayDraw(drawTime) {
  if (!drawTime || !/^\d{1,2}:\d{2}$/.test(drawTime)) return false;
  try {
    const now = new Date();
    const drNow = new Date(now.getTime() + (now.getTimezoneOffset() - 240) * 60000);
    const [h, m] = drawTime.split(":").map((v) => parseInt(v, 10));
    const drMinutes = drNow.getHours() * 60 + drNow.getMinutes();
    return drMinutes >= h * 60 + m;
  } catch {
    return false;
  }
}

function drToday() {
  const now = new Date();
  const drNow = new Date(now.getTime() + (now.getTimezoneOffset() - 240) * 60000);
  const dd = String(drNow.getDate()).padStart(2, "0");
  const mm = String(drNow.getMonth() + 1).padStart(2, "0");
  return { dd, mm, label: `${dd}-${mm}` };
}

function NumberPill({ n, today }) {
  const cls = today
    ? "bg-gradient-to-br from-green-500 to-green-600 text-white border-green-700"
    : "bg-gradient-to-br from-gray-200 to-gray-300 text-gray-700 border-gray-400";
  return (
    <span
      className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${cls} font-extrabold text-base border shadow-sm`}
      style={{ fontFamily: "'Playfair Display', serif" }}
    >
      {n}
    </span>
  );
}

function DrawCard({ lottery, draw, result }) {
  const brand = brandFor(lottery.id, lottery.name, lottery.colors);
  const today = isTodayDraw(draw.time);
  // Show today's DR date when the draw is from today, otherwise yesterday's date
  const drNow = drToday();
  let dateLabel = drNow.label;
  if (!today) {
    const d = new Date();
    const dr = new Date(d.getTime() + (d.getTimezoneOffset() - 240) * 60000);
    dr.setDate(dr.getDate() - 1);
    const dd = String(dr.getDate()).padStart(2, "0");
    const mm = String(dr.getMonth() + 1).padStart(2, "0");
    dateLabel = `${dd}-${mm}`;
  }
  // Server may also send a real day field — prefer that when present
  if (result?.day) {
    const real = dayMonthFromDate(result.day);
    if (real) dateLabel = real;
  }
  // Trim to the declared number count for this draw (some scraped sources include
  // a leading "date" digit pair that doesn't belong to the actual winning numbers)
  const maxCount = draw.count || 8;
  const rawNumbers = result?.numbers || [];
  const numbers = rawNumbers.length > maxCount ? rawNumbers.slice(-maxCount) : rawNumbers.slice(0, maxCount);

  return (
    <div className="rounded-md overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-md transition">
      {/* Brand bar */}
      <div
        className="px-3 py-1.5 text-xs font-extrabold tracking-wide"
        style={{ background: brand.bg, color: brand.fg }}
      >
        {brand.label}
      </div>

      {/* Body */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-wrap">
            {dateLabel && (
              <span className="inline-block px-2 py-0.5 rounded bg-gray-700 text-white text-[11px] font-mono font-bold">
                {dateLabel}
              </span>
            )}
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-400">
              <BarChart3 className="w-3 h-3" /> estadísticas
            </span>
          </div>
          <LotteryLogo lottery={lottery} draw={draw} size={64} />
        </div>

        <p className="text-base sm:text-lg font-extrabold text-gray-800 mb-3 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
          {draw.name}
        </p>

        <div className="flex items-center justify-center gap-2 flex-wrap min-h-[3rem]">
          {numbers.length > 0 ? (
            numbers.map((n, i) => <NumberPill key={i} n={n} today={today} />)
          ) : (
            <span className="text-xs text-gray-400 italic">Pendiente</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LotteriesSection() {
  const [lotteries, setLotteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const { query, submit } = useSearch();
  const [local, setLocal] = useState("");

  useEffect(() => { setLocal(query); }, [query]);

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchLotteries();
      setLotteries(data.lotteries || []);
      setUpdatedAt(new Date());
      setError(null);
    } catch (e) {
      setError("No pudimos cargar los resultados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Flatten: every individual draw becomes its own card
  const cards = useMemo(() => {
    const out = [];
    for (const lot of lotteries) {
      for (const d of (lot.draws || [])) {
        const r = (lot.latest && lot.latest[d.id]) || null;
        out.push({ lottery: lot, draw: d, result: r });
      }
    }
    const q = (local || "").trim().toLowerCase();
    if (!q) return out;
    return out.filter(({ lottery, draw }) =>
      lottery.name.toLowerCase().includes(q) ||
      lottery.short.toLowerCase().includes(q) ||
      lottery.id.toLowerCase().includes(q) ||
      draw.name.toLowerCase().includes(q)
    );
  }, [lotteries, local]);

  const clearSearch = () => { setLocal(""); submit(""); };

  // Always format "today" in DR (UTC-4) regardless of the browser's TZ
  const drNowDate = (() => {
    const d = new Date();
    return new Date(d.getTime() - 4 * 60 * 60 * 1000);
  })();
  const today = drNowDate.toLocaleDateString("es-DO", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
    timeZone: "UTC",
  });

  return (
    <section id="loterias" className="py-16 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-[#0e4a7a] text-xs font-semibold mb-3 border border-blue-100">
            <Bot className="w-3.5 h-3.5" /> ACTUALIZADOR · cada 3 minutos
          </div>
          <h2 className="np-title inline-block text-2xl sm:text-3xl font-extrabold text-[#0e4a7a]">
            Resultados de Loterías Dominicanas
          </h2>
          <p className="text-gray-700 max-w-3xl mx-auto mt-3 text-sm sm:text-base font-semibold capitalize">{today}</p>
          <p className="text-gray-600 max-w-3xl mx-auto mt-2 text-sm sm:text-base">
            Todos los sorteos del día en un mismo lugar. Actualizamos los números cada 3 minutos desde las loterías oficiales.
          </p>
          <div className="mt-3 flex items-center justify-center gap-3 text-xs text-gray-500 flex-wrap">
            {updatedAt && (
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Actualizado {updatedAt.toLocaleTimeString("es-DO")}
              </span>
            )}
            <button
              onClick={load}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 hover:bg-blue-100 text-[#1d6cb1] font-semibold transition"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
            </button>
          </div>
        </div>

        {/* Inline search */}
        <div className="max-w-xl mx-auto mb-8">
          <div className="flex items-center bg-white border-2 border-blue-100 focus-within:border-[#1d6cb1] rounded-full px-4 py-2 shadow-sm transition">
            <Search className="w-4 h-4 text-[#1d6cb1]" />
            <input
              type="text"
              value={local}
              onChange={(e) => setLocal(e.target.value)}
              placeholder="Buscar lotería o sorteo..."
              className="flex-1 bg-transparent outline-none text-sm px-3 py-1.5 text-gray-800"
            />
            {local && (
              <button onClick={clearSearch} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Limpiar">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {local && (
            <p className="text-center text-xs text-gray-500 mt-2">
              {cards.length} sorteo{cards.length === 1 ? "" : "s"} encontrado{cards.length === 1 ? "" : "s"}
            </p>
          )}
        </div>

        {error && (
          <div className="max-w-md mx-auto mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {loading && cards.length === 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 9 }).map((_, i) => (
              <div key={i} className="h-40 rounded-md bg-white/70 animate-pulse border border-gray-200" />
            ))}
          </div>
        ) : cards.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No se encontraron sorteos que coincidan con "{local}".</p>
            <button onClick={clearSearch} className="mt-4 px-4 py-2 rounded-full bg-blue-50 text-[#1d6cb1] font-semibold text-sm hover:bg-blue-100">
              Ver todos los sorteos
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="results-grid">
            {cards.map(({ lottery, draw, result }) => (
              <DrawCard
                key={`${lottery.id}-${draw.id}`}
                lottery={lottery}
                draw={draw}
                result={result}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
